import { actionCreators } from 'app/area/dashboard/actions';
import {
  selectContainerLayout, selectExecutionCounter, selectFetchingMainFavourite,
  selectMainFavouriteId
} from 'app/area/dashboard/selectors';
import { DrillDownModel } from 'app/components/favourite-layout/favourite/common/drill-down-model';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FavouriteDefinition } from 'app/types-shared';
import { api } from './api';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { LocationWithQuery } from 'app/components/enhancers/with-router';

export function useSelectors(dashboardType: number) {
  const containerLayout = useSelector(selectContainerLayout(dashboardType));
  const fetchingMainFavourite = useSelector(selectFetchingMainFavourite);
  const mainFavouriteId = useSelector(selectMainFavouriteId);

  return {
    containerLayout,
    fetchingMainFavourite,
    mainFavouriteId
  };
}

export function useActionCreators() {
  const dispatch = useDispatch();
  return useMemo(() => bindActionCreators(actionCreators, dispatch), [dispatch]);
}

export function useDrillDown(mainFavouriteId: number) {
  const {
    drillDownToCaseListing
  } = useActionCreators();

  return useCallback((favouriteId: number, model: DrillDownModel) => {
    drillDownToCaseListing(mainFavouriteId, favouriteId, model);
  }, [mainFavouriteId]);
}

export function useCreateMainFavouriteEffect(
  dashboardType: number,
  requestedMainFavouriteId: number | undefined,
  mainFavouriteReadOnly: boolean,
  location: LocationWithQuery
) {
  const {
    createMainFavourite,
    clearMainFavourite
  } = useActionCreators();

  useEffect(() => {
    createMainFavourite(dashboardType, requestedMainFavouriteId, mainFavouriteReadOnly, location);
  }, [dashboardType, requestedMainFavouriteId, mainFavouriteReadOnly, location]);

  useEffect(() => () => { clearMainFavourite() }, []);
}

export function useLoadContainerDefinitionsEffect(dashboardType: number, layoutTypeContextId: number) {
  const {
    loadContainerDefinitions
  } = useActionCreators();

  useEffect(() => {
    loadContainerDefinitions(dashboardType, layoutTypeContextId);
  }, [dashboardType, layoutTypeContextId]);
}

export function useDashboardExecuteEffect(definition: FavouriteDefinition) {
  const fetchingMainFavourite = useSelector(selectFetchingMainFavourite);
  const mainFavouriteId = useSelector(selectMainFavouriteId);

  const {
    executeDashboard
  } = useActionCreators();

  useEffect(() => {
    if (!fetchingMainFavourite && mainFavouriteId && definition) {
      executeDashboard();
    }
  }, [fetchingMainFavourite, mainFavouriteId, definition]);

  return useSelector(selectExecutionCounter);
}

export function useReportApiOverrides(mainFavouriteId: number): Partial<ReportCommonApi> {
  return useMemo(() => ({
    executeFavourite: (definition: FavouriteDefinition,
      startIndex?: number, maxResults?: number, ovverideApi?: boolean) =>
      api.executeFavourite(definition, mainFavouriteId, startIndex, maxResults, ovverideApi),
    drilldown: (definition: FavouriteDefinition, model: DrillDownModel) => api.drillDown(mainFavouriteId, definition.favourite.v, model)
  }), [mainFavouriteId]);
}

export function useIsDashboardDisabled(definition?: any) {
  const selectedDashboardDefinition = definition?.reports?.filter(report => report.report.v === definition.selectedReport.v)[0];
  const disabledDate = new Date(selectedDashboardDefinition?.dashboard?.disabled);
  return disabledDate?.getTime() <= new Date().getTime();
}
